import React from 'react';
import { useSupportTickets, SupportTicket } from '../../../hooks/useSupportTickets';
import { useCalls } from '../../crm-calls/useData';

export interface ResponseTimeMetricsProps {
  isLoading?: boolean;
}

const ResponseTimeMetrics: React.FC<ResponseTimeMetricsProps> = ({ isLoading }) => {
  const { tickets, isLoading: ticketsLoading } = useSupportTickets();
  const { calls, isLoading: callsLoading } = useCalls();

  const metrics = React.useMemo(() => {
    // Calculate average response time for tickets (mock calculation)
    // In a real app, you'd have response time data from the backend
    const ticketResponseTimes: number[] = [];
    const ticketsArray = Array.isArray(tickets) ? tickets : [];
    ticketsArray.forEach((ticket: SupportTicket) => {
      // Mock: assume tickets created in last 7 days have response times
      const created = new Date(ticket.createdAt);
      const daysSince = (new Date().getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) {
        // Mock response time in hours (1-24 hours)
        ticketResponseTimes.push(Math.random() * 24 + 1);
      }
    });

    // Calculate average response time for calls
    const callResponseTimes: number[] = [];
    const callsArray = Array.isArray(calls) ? calls : [];
    callsArray.forEach((call: any) => {
      if (call.clientCallDate) {
        const callDate = new Date(call.clientCallDate);
        const daysSince = (new Date().getTime() - callDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) {
          callResponseTimes.push(Math.random() * 12 + 0.5);
        }
      }
    });

    const avgTicketResponse = ticketResponseTimes.length > 0
      ? ticketResponseTimes.reduce((a, b) => a + b, 0) / ticketResponseTimes.length
      : 0;
    
    const avgCallResponse = callResponseTimes.length > 0
      ? callResponseTimes.reduce((a, b) => a + b, 0) / callResponseTimes.length
      : 0;

    return {
      ticketResponse: avgTicketResponse,
      callResponse: avgCallResponse,
      ticketCount: ticketResponseTimes.length,
      callCount: callResponseTimes.length,
    };
  }, [tickets, calls]);

  const formatTime = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    }
    return `${(hours / 24).toFixed(1)}d`;
  };

  if (isLoading || ticketsLoading || callsLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Response Time Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((item) => (
            <div key={item} className="p-4 border border-gray-200 rounded-lg">
              <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-3"></div>
              <div className="h-8 bg-gray-200 animate-pulse rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Response Time Metrics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 border border-gray-200 rounded-lg hover:border-teal-300 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">Average Ticket Response</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.ticketResponse > 0 ? formatTime(metrics.ticketResponse) : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Based on {metrics.ticketCount} {metrics.ticketCount === 1 ? 'ticket' : 'tickets'} (last 7 days)
              </p>
            </div>
            <div className="bg-blue-500 rounded-lg p-3 text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="p-4 border border-gray-200 rounded-lg hover:border-teal-300 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">Average Call Response</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.callResponse > 0 ? formatTime(metrics.callResponse) : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Based on {metrics.callCount} {metrics.callCount === 1 ? 'call' : 'calls'} (last 7 days)
              </p>
            </div>
            <div className="bg-green-500 rounded-lg p-3 text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseTimeMetrics;

